import { init, track, setUserId, identify, Identify } from '@amplitude/analytics-browser';

// Check if we're in development environment
const isDevelopment = import.meta.env.DEV;

// Analytics state
let isAnalyticsEnabled = false;
let isInitialized = false;

// Initialize Amplitude only if consent is given and not in development
export const initializeAnalytics = (hasConsent: boolean = false) => {
  // Don't initialize in development or without consent
  if (isDevelopment || !hasConsent) {
    console.log('Analytics disabled:', { isDevelopment, hasConsent });
    return;
  }

  if (isInitialized) {
    return;
  }

  try {
    init('f5256554e3f3e11935f570ccc15016ff', {
      autocapture: {
        attribution: true,
        fileDownloads: true,
        formInteractions: true,
        pageViews: true,
        sessions: true,
        elementInteractions: true,
        networkTracking: true,
        webVitals: true,
        frustrationInteractions: true
      },
      serverZone: 'EU'
    });
    isAnalyticsEnabled = true;
    isInitialized = true;
    console.log('Analytics initialized successfully');
  } catch (error) {
    console.error('Failed to initialize analytics:', error);
  }
};

// Update analytics state based on consent
export const updateAnalyticsConsent = (hasConsent: boolean) => {
  if (isDevelopment) {
    isAnalyticsEnabled = false;
    return;
  }

  isAnalyticsEnabled = hasConsent;

  if (!hasConsent && isInitialized) {
    // Note: Amplitude doesn't have a direct way to disable tracking
    // We'll handle this by not calling track functions
    console.log('Analytics tracking disabled due to consent withdrawal');
  }
};

// Safe track function that respects consent
const safeTrack = (eventName: string, properties?: Record<string, any>) => {
  if (!isAnalyticsEnabled || isDevelopment) {
    console.log('Analytics tracking skipped:', { eventName, isAnalyticsEnabled, isDevelopment });
    return;
  }

  try {
    track(eventName, properties);
  } catch (error) {
    console.error('Analytics tracking error:', error);
  }
};

// Event tracking functions
export const analytics = {
  // Page and navigation events
  pageViewed: (pageName: string, properties?: Record<string, any>) => {
    safeTrack('Page Viewed', {
      page_name: pageName,
      ...properties
    });
  },

  // Search and scan events
  searchInitiated: (searchType: 'single' | 'bulk' | 'file', domain?: string, domainCount?: number) => {
    safeTrack('Search Initiated', {
      search_type: searchType,
      domain: domain || null,
      domain_count: domainCount || null,
      timestamp: new Date().toISOString()
    });
  },

  searchCompleted: (searchType: 'single' | 'bulk' | 'file', resultsCount: number, duration?: number) => {
    safeTrack('Search Completed', {
      search_type: searchType,
      results_count: resultsCount,
      duration_ms: duration || null,
      timestamp: new Date().toISOString()
    });
  },

  searchFailed: (searchType: 'single' | 'bulk' | 'file', error: string, domain?: string) => {
    safeTrack('Search Failed', {
      search_type: searchType,
      error_message: error,
      domain: domain || null,
      timestamp: new Date().toISOString()
    });
  },

  // Domain interaction events
  domainClicked: (domain: string, isAvailable: boolean, position?: number) => {
    safeTrack('Domain Clicked', {
      domain,
      is_available: isAvailable,
      position: position || null,
      timestamp: new Date().toISOString()
    });
  },

  domainCopied: (domain: string, isAvailable: boolean) => {
    safeTrack('Domain Copied', {
      domain,
      is_available: isAvailable,
      timestamp: new Date().toISOString()
    });
  },

  domainFiltered: (filterType: string, filterValue: any, resultsCount: number) => {
    safeTrack('Domain Filtered', {
      filter_type: filterType,
      filter_value: filterValue,
      results_count: resultsCount,
      timestamp: new Date().toISOString()
    });
  },

  // UI interaction events
  tabSwitched: (fromTab: string, toTab: string) => {
    safeTrack('Tab Switched', {
      from_tab: fromTab,
      to_tab: toTab,
      timestamp: new Date().toISOString()
    });
  },

  resultsCleared: (resultsCount: number) => {
    safeTrack('Results Cleared', {
      results_count: resultsCount,
      timestamp: new Date().toISOString()
    });
  },

  // File upload events
  fileUploaded: (fileName: string, fileSize: number, domainCount: number) => {
    safeTrack('File Uploaded', {
      file_name: fileName,
      file_size: fileSize,
      domain_count: domainCount,
      timestamp: new Date().toISOString()
    });
  },

  // Error events
  errorOccurred: (errorType: string, errorMessage: string, context?: Record<string, any>) => {
    safeTrack('Error Occurred', {
      error_type: errorType,
      error_message: errorMessage,
      context: context || {},
      timestamp: new Date().toISOString()
    });
  },

  // User engagement events
  sessionStarted: () => {
    safeTrack('Session Started', {
      timestamp: new Date().toISOString()
    });
  },

  sessionEnded: (duration: number, actionsCount: number) => {
    safeTrack('Session Ended', {
      duration_ms: duration,
      actions_count: actionsCount,
      timestamp: new Date().toISOString()
    });
  },

  // Feature usage events
  featureUsed: (featureName: string, properties?: Record<string, any>) => {
    safeTrack('Feature Used', {
      feature_name: featureName,
      ...properties,
      timestamp: new Date().toISOString()
    });
  },

  // Performance events
  scanProgress: (progress: number, totalDomains: number, processedDomains: number) => {
    safeTrack('Scan Progress', {
      progress_percentage: progress,
      total_domains: totalDomains,
      processed_domains: processedDomains,
      timestamp: new Date().toISOString()
    });
  },

  // Custom event tracking
  customEvent: (eventName: string, properties?: Record<string, any>) => {
    safeTrack(eventName, {
      ...properties,
      timestamp: new Date().toISOString()
    });
  },

  // Valuation-related events
  valuationViewed: (domain: string, valuationTier: string, confidence: string) => {
    safeTrack('Valuation Viewed', {
      domain,
      valuation_tier: valuationTier,
      confidence,
      timestamp: new Date().toISOString()
    });
  },

  valuationColumnSorted: (sortOrder: string, resultsCount: number) => {
    safeTrack('Valuation Column Sorted', {
      sort_order: sortOrder,
      results_count: resultsCount,
      timestamp: new Date().toISOString()
    });
  },

  valuationBadgeClicked: (domain: string, valuationTier: string) => {
    safeTrack('Valuation Badge Clicked', {
      domain,
      valuation_tier: valuationTier,
      timestamp: new Date().toISOString()
    });
  },

  featureBadgeClicked: (badgeName: string) => {
    safeTrack('Feature Badge Clicked', {
      badge_name: badgeName,
      timestamp: new Date().toISOString()
    });
  },

  // User identification
  identifyUser: (userId: string, userProperties?: Record<string, any>) => {
    if (!isAnalyticsEnabled || isDevelopment) {
      console.log('User identification skipped:', { isAnalyticsEnabled, isDevelopment });
      return;
    }

    try {
      setUserId(userId);
      if (userProperties) {
        const identifyInstance = new Identify();
        Object.entries(userProperties).forEach(([key, value]) => {
          identifyInstance.set(key, value);
        });
        identify(identifyInstance);
      }
    } catch (error) {
      console.error('User identification error:', error);
    }
  }
};

// Hook for easy analytics usage in React components
export const useAnalytics = () => {
  return analytics;
};
